import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { CoordinatesFormatterService, FormattedCoordinates } from "@app/core/services/coordinates-formatter.service";
import { SolvedCoordinatesService, AdvancedSolutionMatrix } from "@app/core/services/solved-coordinates.service";
import { fromEvent, Subject, of, combineLatest, Subscription } from "rxjs";
import { switchMap, takeUntil, throttleTime, startWith, filter } from "rxjs/operators";

export interface SkyCoords {
  raHtml: string;
  decHtml: string;
  galacticRaHtml: string;
  galacticDecHtml: string;
  event?: MouseEvent;
}

@Directive({
  selector: "[astrobinCoordinatesTracker]"
})
export class CoordinatesTrackerDirective implements OnInit, OnChanges, OnDestroy {
  @Input() solutionId?: number;
  @Input() solutionMatrix?: AdvancedSolutionMatrix;
  @Input() eventElement?: HTMLElement | null = null; // Optional element to listen for events (default: host element)
  @Input() showGalactic = true;

  @Output() coords = new EventEmitter<SkyCoords>();

  private readonly destroy$ = new Subject<void>();
  private readonly matrix$ = new Subject<AdvancedSolutionMatrix>();
  private readonly THROTTLE_TIME_MS = 20;

  private matrixSubscription?: Subscription;
  private coordsSubscription?: Subscription;

  constructor(
    private readonly coordinatesService: SolvedCoordinatesService,
    private readonly coordinatesFormatter: CoordinatesFormatterService,
    private readonly elementRef: ElementRef,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.setupMouseTracking();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If inputs related to the matrix changed, update the matrix observable
    if (changes.solutionMatrix || changes.solutionId) {
      this.cleanupMatrixSubscription();
      this.setupMatrixObservable();
    }

    // If image element or event element changed, we need to set up the mouse tracking again
    if (changes.imageElement || changes.eventElement) {
      this.setupMouseTrackingForImage();
    }
  }

  ngOnDestroy(): void {
    this.cleanupMatrixSubscription();
    this.cleanupCoordsSubscription();

    this.destroy$.next();
    this.destroy$.complete();
  }

  private cleanupMatrixSubscription(): void {
    if (this.matrixSubscription) {
      this.matrixSubscription.unsubscribe();
      this.matrixSubscription = undefined;
    }
  }

  private cleanupCoordsSubscription(): void {
    if (this.coordsSubscription) {
      this.coordsSubscription.unsubscribe();
      this.coordsSubscription = undefined;
    }
  }

  private setupMatrixObservable(): void {
    // If solutionMatrix is provided directly, use it
    if (this.solutionMatrix) {
      this.matrix$.next(this.solutionMatrix);
    }
    // If solutionId is provided, load the matrix via service
    else if (this.solutionId) {
      this.matrixSubscription = this.coordinatesService
        .ensureMatrixLoaded(this.solutionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(matrix => {
          this.matrix$.next(matrix);
        });
    }
  }

  private setupMouseTracking(): void {
    this.setupMouseTrackingForImage();
  }

  /**
   * This sets up the mouse tracking whenever the imageElement changes
   */
  private setupMouseTrackingForImage(): void {
    // Clean up any existing subscription
    this.cleanupCoordsSubscription();

    // We'll run the actual event handling outside Angular's zone for performance
    this.ngZone.runOutsideAngular(() => {
      const eventSource = this.eventElement || this.elementRef.nativeElement;

      // Create an observable for the mouse move events with throttling
      const mouseMove$ = fromEvent<MouseEvent>(eventSource, "mousemove").pipe(
        throttleTime(this.THROTTLE_TIME_MS),
        takeUntil(this.destroy$)
      );

      // Combine the latest matrix and mouse move event
      this.coordsSubscription = combineLatest([this.matrix$.pipe(startWith(this.solutionMatrix)), mouseMove$])
        .pipe(
          // Filter out cases where matrix is missing
          filter(([matrix]) => !!matrix && !!matrix.raMatrix && !!matrix.decMatrix && !!matrix.matrixRect),
          switchMap(([matrix, event]) => {
            // Calculate coordinates using the formatter service
            const result = this.coordinatesFormatter.calculateMouseCoordinates(
              event,
              this.eventElement || this.elementRef.nativeElement,
              matrix
            );

            if (result) {
              return of({
                coordinates: result.coordinates,
                event: event
              });
            }

            return of(null);
          }),
          // Ensure type safety by filtering out null values
          filter((result): result is { coordinates: FormattedCoordinates; event: MouseEvent } => result !== null),
          takeUntil(this.destroy$)
        )
        .subscribe(result => {
          // Run back inside Angular zone to emit the output
          this.ngZone.run(() => {
            this.coords.emit({
              raHtml: result.coordinates.raHtml,
              decHtml: result.coordinates.decHtml,
              galacticRaHtml: result.coordinates.galacticRaHtml,
              galacticDecHtml: result.coordinates.galacticDecHtml,
              event: result.event
            });
          });
        });
    });
  }
}
